import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../components/header";
import Footer from "../components/footer";
import Loader from "../components/loader";
import { blogService } from "../../services/blogService";
import { useSearchSEO } from "../../hooks/useSEO";

const BlogSearch = () => {
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [blogs, setBlogs] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const postsPerPage = 9;
  const query = searchParams.get('q') || '';

  // Use SEO hook for search page
  useSearchSEO(query, blogs);

  useEffect(() => {
    setSearchQuery(query);
    if (query.trim()) {
      performSearch(query, 1);
    }
  }, [query]);

  useEffect(() => {
    if (query.trim()) {
      performSearch(query, currentPage);
    }
  }, [currentPage]);

  const performSearch = async (searchTerm, page = 1) => {
    if (!searchTerm.trim()) {
      setBlogs([]);
      setTotalPages(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: page,
        limit: postsPerPage
      };

      const searchData = await blogService.searchBlogs(searchTerm, params);
      setBlogs(searchData.blogs || []);
      setTotalPages(Math.ceil((searchData.total || 0) / postsPerPage));
      
    } catch (error) {
      console.error('Error searching blogs:', error);
      setError('Failed to search blog posts. Please try again later.');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() });
      setCurrentPage(1);
      setShowSuggestions(false);
    }
  };

  const handleInputChange = async (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Show suggestions for queries with 2+ characters
    if (value.length >= 2) {
      try {
        const searchData = await blogService.searchBlogs(value, { limit: 5 });
        const suggestionTitles = searchData.blogs?.map(blog => blog.title).slice(0, 5) || [];
        setSuggestions(suggestionTitles);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setSearchParams({ q: suggestion });
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const handleReadMore = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const getCategoryDisplayName = (category) => {
    if (typeof category === 'string') {
      return category.charAt(0).toUpperCase() + category.slice(1);
    }
    if (category && category.name) {
      return category.name.charAt(0).toUpperCase() + category.name.slice(1);
    }
    return 'General';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="ErrorMessage">
      <div className="text-center py-5">
        <i className="fas fa-exclamation-triangle fa-3x mb-3" style={{color: '#ff6b6b'}}></i>
        <h4 className="Head_4 mb-3">Oops! Something went wrong</h4>
        <p className="Pra_1 mb-4">{message}</p>
        <button className="BtnFill" onClick={onRetry}>
          <i className="fas fa-redo me-2"></i>
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Header />
      
      {/* Breadcrumb Navigation */}
      <section className="BreadcrumbSection">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                  <i className="fas fa-home me-2"></i>Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }}>
                  Blog
                </a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                <i className="fas fa-search me-2"></i>Search
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Search Hero Section */}
      <section className="BlogSearchHeroSection">
        <div className="container">
          <div className="CustomHeading text-center">
            <h1 className="Head_1">
              <i className="fas fa-search me-3"></i>
              Search Blog
            </h1>
            {query ? (
              <h2 className="Head_2">Results for "{query}"</h2>
            ) : (
              <h2 className="Head_2">Find the perfect article</h2>
            )}
          </div>

          {/* Search Form */}
          <div className="SearchForm">
            <form onSubmit={handleSearch} className="search-form-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search for articles, tips, destinations..."
                  value={searchQuery}
                  onChange={handleInputChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
                
                {/* Search Suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="search-suggestions">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <i className="fas fa-search me-2"></i>
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Search Results Section */}
      <section className="BlogSearchResultsSection">
        <div className="container">
          {/* Loading State */}
          {loading && <Loader />}

          {/* Error Message */}
          {error && (
            <ErrorMessage 
              message={error} 
              onRetry={() => {
                setError(null);
                if (query.trim()) {
                  performSearch(query, currentPage);
                }
              }} 
            />
          )}

          {/* Search Results */}
          {!loading && !error && query && (
            <>
              {/* Results Count */}
              <div className="SearchResultsHeader">
                <p className="results-count">
                  {blogs.length > 0 
                    ? `Found ${blogs.length} article${blogs.length !== 1 ? 's' : ''} for "${query}"`
                    : `No articles found for "${query}"`
                  }
                </p>
              </div>

              {/* Blog Grid */}
              {blogs.length > 0 && (
                <div className="BlogGrid">
                  <div className="row">
                    {blogs.map((post) => (
                      <div key={post._id || post.id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4">
                        <article className="BlogCard">
                          <div className="BlogCardImage">
                            <img src={post.featuredImage || post.image} alt={post.title} loading="lazy" />
                            <div className="CategoryBadge">
                              {post.categories && post.categories.length > 0 
                                ? getCategoryDisplayName(post.categories[0])
                                : getCategoryDisplayName(post.category || 'general')
                              }
                            </div>
                          </div>
                          <div className="BlogCardContent">
                            <div className="BlogMeta">
                              <span className="Date">
                                <i className="fas fa-calendar-alt me-2"></i>
                                {formatDate(post.publishDate || post.createdAt)}
                              </span>
                              <span className="ReadTime">
                                <i className="fas fa-clock me-2"></i>
                                {post.readingTime || post.readTime || 5} min
                              </span>
                            </div>
                            <h4 
                              className="Head_5"
                              dangerouslySetInnerHTML={{ 
                                __html: highlightSearchTerm(post.title, query) 
                              }}
                            />
                            <p 
                              className="Pra_1"
                              dangerouslySetInnerHTML={{ 
                                __html: highlightSearchTerm(post.excerpt || post.summary || '', query) 
                              }}
                            />
                            <button 
                              className="ReadMoreBtn"
                              onClick={() => handleReadMore(post.slug)}
                            >
                              Read More
                              <i className="fas fa-arrow-right ms-2"></i>
                            </button>
                          </div>
                        </article>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="BlogPagination">
                  <nav aria-label="Search results pagination">
                    <div className="pagination-wrapper">
                      <button
                        className={`pagination-btn prev ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left me-2"></i>
                        Previous
                      </button>
                      
                      <div className="page-numbers">
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          return (
                            <button
                              key={pageNumber}
                              className={`page-number ${currentPage === pageNumber ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        className={`pagination-btn next ${currentPage === totalPages ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <i className="fas fa-chevron-right ms-2"></i>
                      </button>
                    </div>
                  </nav>
                </div>
              )}

              {/* No Results Message */}
              {blogs.length === 0 && (
                <div className="NoResults">
                  <div className="text-center py-5">
                    <i className="fas fa-search fa-3x mb-3" style={{color: '#ff9900'}}></i>
                    <h4 className="Head_4 mb-3">No articles found</h4>
                    <p className="Pra_1 mb-4">
                      We couldn't find any articles matching "{query}". Try different keywords or browse our categories.
                    </p>
                    <div className="search-suggestions-actions">
                      <button 
                        className="BtnBorder me-3"
                        onClick={() => navigate('/blog')}
                      >
                        View All Articles
                      </button>
                      <button 
                        className="BtnFill"
                        onClick={() => {
                          setSearchQuery('');
                          setSearchParams({});
                          document.querySelector('.search-input')?.focus();
                        }}
                      >
                        Try New Search
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Initial State - No Search Query */}
          {!loading && !error && !query && (
            <div className="SearchInitialState">
              <div className="text-center py-5">
                <i className="fas fa-search fa-3x mb-3" style={{color: '#ff9900'}}></i>
                <h4 className="Head_4 mb-3">Start your search</h4>
                <p className="Pra_1 mb-4">
                  Enter keywords above to find articles about car rentals, travel tips, and Perth destinations.
                </p>
                <div className="popular-searches">
                  <h5 className="mb-3">Popular searches:</h5>
                  <div className="search-tags">
                    {['Perth travel', 'Car rental tips', 'Road trips', 'Long term rental', 'Airport pickup'].map((tag, index) => (
                      <button
                        key={index}
                        className="search-tag"
                        onClick={() => {
                          setSearchQuery(tag);
                          setSearchParams({ q: tag });
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BlogSearch;